import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Camera,
  Check,
  Coins,
  Film,
  Image as ImageIcon,
  LayoutTemplate,
  Mic2,
  Minus,
  Palette,
  Plus,
  BookmarkPlus,
  Scissors,
  Search,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";

/**
 * True on a phone-sized screen. matchMedia rather than a width state, so it costs
 * one listener instead of a re-render per resize pixel.
 */
function useIsNarrow() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 640px)");
    const update = () => setNarrow(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return narrow;
}
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { useLanguage, useT } from "../i18n";
import { useServiceText } from "../service-text";
import Amount from "./Amount";
import type { Service } from "../types";
import { formatCredits, formatKd } from "../utils";

type CategoryVisual = {
  icon: LucideIcon;
  image: string;
  /** The SHORT chip label, not the category's full name — "Video", not "Video and
   *  motion". `labelAr` is its Arabic twin for the same reason: the full Arabic
   *  category would wrap the chip onto two lines. */
  label: string;
  labelAr: string;
};

const categoryVisuals: Record<string, CategoryVisual> = {
  "Included services": { icon: LayoutTemplate, image: "/brand/service-thumbnails/design.webp", label: "Included", labelAr: "مشمولة" },
  "Animated content": { icon: Sparkles, image: "/brand/service-thumbnails/design.webp", label: "Animated", labelAr: "متحرك" },
  "Video and motion": { icon: Film, image: "/brand/service-thumbnails/production.webp", label: "Video", labelAr: "فيديو" },
  "Audio and voice": { icon: Mic2, image: "/brand/service-thumbnails/audio.webp", label: "Audio", labelAr: "صوت" },
  "Talent and styling": { icon: Users, image: "/brand/service-thumbnails/talent.webp", label: "Talent", labelAr: "مواهب" },
  "Post-production": { icon: Scissors, image: "/brand/service-thumbnails/creative.webp", label: "Editing", labelAr: "مونتاج" },
  "Photography and coverage": { icon: Camera, image: "/brand/service-thumbnails/production.webp", label: "Photography", labelAr: "تصوير" },
  "Campaign and creative": { icon: Palette, image: "/brand/service-thumbnails/creative.webp", label: "Creative", labelAr: "إبداع" },
  "Account management": { icon: BriefcaseBusiness, image: "/brand/service-thumbnails/account.webp", label: "Account", labelAr: "الحساب" },
};

const fallbackVisual: CategoryVisual = {
  icon: ImageIcon,
  image: "/brand/service-thumbnails/design.webp",
  label: "Production",
  labelAr: "إنتاج",
};

const featuredIds = [
  "static-social-designs",
  "animated-gif-20",
  "video-production-reel",
  "motion-option-one",
  "photo-session",
  "event-coverage-bundle",
  "campaign-key-visual",
  "creative-concept",
];

const categoryOrder = [
  "Video and motion",
  "Photography and coverage",
  "Animated content",
  "Campaign and creative",
  "Included services",
  "Post-production",
  "Audio and voice",
  "Talent and styling",
  "Account management",
];

/** Everything a caller may pre-fill. The due date is absent on purpose: a copy of a
 *  request from July must not arrive carrying July's deadline. */
export type RequestPrefill = {
  serviceId: string;
  title: string;
  brief: string;
  quantity: number;
  language: string;
  channel: string | null;
  referenceUrl: string | null;
};

export default function RequestForm({
  services,
  month,
  initialServiceId,
  prefill,
  onCreated,
}: {
  services: Service[];
  month: string;
  initialServiceId?: string;
  /** Pre-filled values from a duplicated request or a saved template. */
  prefill?: RequestPrefill;
  onCreated: () => void;
}) {
  const t = useT();
  const st = useServiceText();
  const uiLanguage = useLanguage();
  const [serviceId, setServiceId] = useState(prefill?.serviceId || initialServiceId || "");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [title, setTitle] = useState(prefill?.title || "");
  const [brief, setBrief] = useState(prefill?.brief || "");
  const [quantity, setQuantity] = useState(prefill?.quantity || 1);
  const [dueDate, setDueDate] = useState("");
  const [language, setLanguage] = useState(prefill?.language || "Arabic & English");
  const [channel, setChannel] = useState(prefill?.channel || "");
  const [referenceUrl, setReferenceUrl] = useState(prefill?.referenceUrl || "");
  const [submitting, setSubmitting] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const isNarrow = useIsNarrow();

  useEffect(() => {
    setServiceId(initialServiceId || "");
  }, [initialServiceId]);

  // A prefill that arrives after the first render — it is fetched, so it always
  // does — has to land in the fields. Guarded on the object identity rather than
  // fired every render, or every keystroke would be overwritten by the original.
  useEffect(() => {
    if (!prefill) return;
    setServiceId(prefill.serviceId);
    setTitle(prefill.title);
    setBrief(prefill.brief);
    setQuantity(prefill.quantity);
    setLanguage(prefill.language);
    setChannel(prefill.channel || "");
    setReferenceUrl(prefill.referenceUrl || "");
  }, [prefill]);

  const selected = useMemo(() => services.find((service) => service.id === serviceId), [serviceId, services]);
  const categories = useMemo(
    () => [...new Set(services.map((service) => service.category))].sort(
      (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b),
    ),
    [services],
  );
  const filtered = useMemo(() => services.filter((service) => {
    const matchesCategory = category === "All" || service.category === category;
    // Both languages, whichever is on screen — see src/service-text.ts.
    const haystack = st.haystack(service);
    return matchesCategory && (!search.trim() || haystack.includes(search.trim().toLowerCase()));
  }), [category, search, services, st]);
  const featured = useMemo(
    () => featuredIds.map((id) => services.find((service) => service.id === id)).filter(Boolean) as Service[],
    [services],
  );
  const estimated = (selected?.credits || 0) * quantity;

  function chooseService(nextService: Service) {
    setServiceId(nextService.id);
    setQuantity(1);
  }

  async function saveTemplate() {
    if (!selected) return;
    // Saving needs a name, and the fields it saves must already be valid — a
    // template of an empty brief is a trap that only springs when someone uses it.
    if (title.trim().length < 2 || brief.trim().length < 2) {
      toast.error("Add a title and a brief before saving this as a template.");
      return;
    }
    const name = window.prompt("Name this brief so you can find it again", title.trim().slice(0, 60));
    if (name === null) return;
    if (name.trim().length < 2) { toast.error("Give the template a name of at least two characters."); return; }

    setSavingTemplate(true);
    try {
      await dashboardApi.saveTemplate({
        name: name.trim(), serviceId: selected.id, title, brief, quantity, language, channel, referenceUrl,
      });
      // Says "saved", not "created": re-using a name overwrites that template on
      // purpose, and claiming a new one appeared would be a lie half the time.
      toast.success(`Saved "${name.trim()}" to your briefs.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.saveTemplate"));
    } finally {
      setSavingTemplate(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const result = await dashboardApi.createRequest({
        serviceId: selected.id,
        title,
        brief,
        quantity,
        dueDate: dueDate || null,
        language,
        channel,
        referenceUrl,
        billingMonth: month,
      });
      toast.success(t("toast.requestSubmitted", { credits: formatCredits(result.estimatedCredits) }));
      onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.submitRequest"));
    } finally {
      setSubmitting(false);
    }
  }

  if (!selected) {
    const showFeatured = category === "All" && !search.trim();
    const regularServices = showFeatured
      ? filtered.filter((service) => !featuredIds.includes(service.id))
      : filtered;

    // On a phone the full catalogue is a seven-metre scroll before the reader
    // reaches whatever they came for. Browsing everything is a deliberate act
    // there, not the default: the eight most-requested services and the search box
    // answer almost every visit, and the rest is one tap away. Untouched on a
    // desktop, where the grid is four wide and the whole list is a few screens.
    const collapsed = isNarrow && showFeatured && !showAll;
    const hiddenCount = regularServices.length;

    return (
      <div className="service-picker">
        <RequestSteps active={1} />
        <div className="service-picker-heading">
          <div>
            <span className="section-eyebrow">{t("picker.eyebrow")}</span>
            <h3>{t("picker.title")}</h3>
            <p>{t("picker.intro")}</p>
          </div>
          <label className="service-picker-search">
            <Search size={18} />
            {/* The count was the literal 47. It is the catalogue's size, so it must
                come from the catalogue — a hardcoded figure becomes a lie the first
                time a service is added or retired. */}
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("picker.search", { n: services.length })} aria-label={t("picker.searchLabel")} autoFocus />
          </label>
        </div>

        <div className="service-category-menu" aria-label={t("picker.categories")}>
          <button type="button" className={category === "All" ? "active" : ""} onClick={() => setCategory("All")}>
            <Sparkles size={16} /> {t("picker.all")}
          </button>
          {categories.map((item) => {
            const visual = categoryVisuals[item] || fallbackVisual;
            const Icon = visual.icon;
            return (
              <button type="button" key={item} className={category === item ? "active" : ""} onClick={() => setCategory(item)}>
                <Icon size={16} /> {uiLanguage === "ar" ? visual.labelAr : visual.label}
              </button>
            );
          })}
        </div>

        <div className="service-menu-scroll">
          {showFeatured && (
            <ServiceSection title={t("picker.featured")} subtitle={t("picker.featuredSub")}>
              {featured.map((service) => <ServiceCard key={service.id} service={service} onSelect={chooseService} featured />)}
            </ServiceSection>
          )}

          {collapsed ? (
            <button type="button" className="button button-secondary show-all-services" onClick={() => setShowAll(true)}>
              {t("picker.browseAll", { n: hiddenCount })}
            </button>
          ) : (
            <ServiceSection
              title={showFeatured ? t("picker.more") : category === "All" ? t("picker.results") : st.category(category)}
              subtitle={t("picker.optionsAvailable", { n: regularServices.length })}
            >
              {regularServices.map((service) => <ServiceCard key={service.id} service={service} onSelect={chooseService} />)}
            </ServiceSection>
          )}

          {!filtered.length && (
            <div className="service-menu-empty">
              <Search size={24} />
              <strong>{t("picker.emptyTitle")}</strong>
              <span>{t("picker.emptyBody")}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const visual = categoryVisuals[selected.category] || fallbackVisual;
  const SelectedIcon = visual.icon;

  return (
    <form className="request-brief-flow" onSubmit={submit}>
      <RequestSteps active={2} />

      <section className="selected-service-hero">
        <img src={visual.image} alt="" />
        <div className="selected-service-shade" />
        <div className="selected-service-copy">
          <button type="button" className="change-service-button" onClick={() => setServiceId("")}>
            <ArrowLeft size={16} /> {t("form.changeService")}
          </button>
          <div className="selected-service-title">
            <div className="selected-service-icon"><SelectedIcon size={21} /></div>
            <div>
              <span>{selected.category}</span>
              <h3>{selected.name}</h3>
              <p>{selected.description}</p>
            </div>
          </div>
        </div>
        <div className="selected-service-rate">
          <span>{selected.included ? t("form.includedService") : t("form.rateEstimate")}</span>
          <strong>{selected.included ? t("form.noDeduction") : `${formatCredits(selected.credits)} credits`}</strong>
          <small>{selected.included ? selected.unit : `${formatCredits(selected.credits)} credits · ${selected.unit}`}</small>
        </div>
      </section>

      <div className="request-brief-fields">
        <div className="brief-fields-heading">
          <div><span className="section-eyebrow">{t("form.essentials")}</span><h4>{t("form.essentialsIntro")}</h4></div>
          <div className="quantity-stepper" aria-label={t("form.quantityLabel")}>
            <span>{t("form.quantity")}</span>
            <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} title={t("form.decrease")} aria-label={t("form.decrease")}><Minus size={15} /></button>
            <strong>{quantity}</strong>
            <button type="button" onClick={() => setQuantity(Math.min(50, quantity + 1))} title={t("form.increase")} aria-label={t("form.increase")}><Plus size={15} /></button>
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="request-title">{t("form.requestTitle")}</label>
          <input id="request-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t("form.titlePlaceholder", { service: selected.name })} required autoFocus />
        </div>

        <div className="form-field">
          <label htmlFor="request-brief">{t("form.brief")}</label>
          <textarea id="request-brief" rows={3} value={brief} onChange={(event) => setBrief(event.target.value)} placeholder={t("form.briefPlaceholder")} required />
        </div>

        <div className="request-detail-row">
          <label className="compact-input">
            <CalendarDays size={17} />
            <span>{t("form.dueDate")}</span>
            <input id="request-due" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} aria-label={t("form.dueDateLabel")} />
          </label>
          <label className="compact-input">
            <LayoutTemplate size={17} />
            <span>{t("form.channel")}</span>
            <input id="request-channel" value={channel} onChange={(event) => setChannel(event.target.value)} placeholder={t("form.channelPlaceholder")} aria-label={t("form.channelLabel")} />
          </label>
          <label className="compact-input">
            <Mic2 size={17} />
            <span>{t("form.language")}</span>
            <select id="request-language" value={language} onChange={(event) => setLanguage(event.target.value)} aria-label={t("form.language")}>
              {/* THE VALUE IS WHAT GETS STORED. These options had no value
                  attribute, so the option TEXT was written to requests.language —
                  and translating the text (which I did earlier in this sweep)
                  meant an Arabic client would store "عربي" where every other
                  screen, the statement and the PDF expect "Arabic". Values are
                  pinned in English; only the label translates. */}
              <option value="Arabic & English">{t("form.langBoth")}</option>
              <option value="Arabic">{t("form.arabic")}</option>
              <option value="English">{t("form.english")}</option>
            </select>
          </label>
        </div>

        <div className="form-field reference-field">
          <label htmlFor="request-reference">{t("form.referenceLink")} <span>{t("form.optional")}</span></label>
          <input id="request-reference" type="url" value={referenceUrl} onChange={(event) => setReferenceUrl(event.target.value)} placeholder={t("form.referencePlaceholder")} />
        </div>
      </div>

      <footer className="request-submit-bar">
        <div className="request-total">
          <div className="estimate-icon"><Coins size={19} /></div>
          <div>
            <span>{quantity > 1 ? `${quantity} × ${formatCredits(selected.credits)} credits` : t("form.estimatedDeduction")}</span>
            <strong>{selected.included ? t("form.includedInHours") : `${formatCredits(estimated)} credits`}</strong>
          </div>
        </div>
        <div className="request-submit-actions">
          {/* type="button" — inside a form, a bare button submits it, which would
              raise the request instead of saving the brief. */}
          <button type="button" className="button button-secondary" onClick={() => void saveTemplate()} disabled={savingTemplate || submitting}>
            <BookmarkPlus size={17} /> {savingTemplate ? t("form.savingBrief") : t("form.saveAsBrief")}
          </button>
          <button className="button button-primary request-submit-button" type="submit" disabled={submitting}>
            {submitting ? t("form.submitting") : t("form.submitRequest")} <ArrowRight size={18} />
          </button>
        </div>
      </footer>
    </form>
  );
}

function RequestSteps({ active }: { active: 1 | 2 }) {
  const t = useT();
  return (
    <div className="request-steps" aria-label={t("form.stepOf", { n: active })}>
      <div className={active >= 1 ? "active" : ""}><span>{active > 1 ? <Check size={13} /> : "1"}</span><strong>{t("form.chooseService")}</strong></div>
      <i />
      <div className={active >= 2 ? "active" : ""}><span>2</span><strong>{t("form.addBrief")}</strong></div>
    </div>
  );
}

function ServiceSection({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section className="service-menu-section">
      <header><div><h4>{title}</h4><span>{subtitle}</span></div></header>
      <div className="service-card-grid">{children}</div>
    </section>
  );
}

function ServiceCard({ service, onSelect, featured = false }: { service: Service; onSelect: (service: Service) => void; featured?: boolean }) {
  const t = useT();
  const st = useServiceText();
  const uiLanguage = useLanguage();
  const visual = categoryVisuals[service.category] || fallbackVisual;
  const Icon = visual.icon;
  return (
    <button type="button" className={`service-menu-card ${featured ? "featured" : ""}`} onClick={() => onSelect(service)}>
      <span className="service-card-image">
        <img src={visual.image} alt="" loading="lazy" />
        <i><Icon size={18} /></i>
        {service.included && <em>{t("catalogue.included")}</em>}
      </span>
      <span className="service-card-copy">
        <small>{uiLanguage === "ar" ? visual.labelAr : visual.label}</small>
        <strong>{st.name(service.id)}</strong>
        <span>{st.description(service.id)}</span>
      </span>
      <span className="service-card-price">
        {/* <Amount> rather than `${credits} cr`: in Arabic the unit is a neutral run
            beside an isolated number and swaps sides — "8.5 cr" renders "cr 8.5". */}
        <b>{service.included ? t("catalogue.included") : <Amount value={service.credits} unit={t("common.cr")} />}</b>
        <ArrowRight size={16} />
      </span>
    </button>
  );
}
